import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { cn } from '../lib/utils';
import { contractsAPI, customersAPI, companiesAPI, contractTypesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import {
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2,
  Upload,
  UserPlus,
  FileText,
  Calendar as CalendarIcon,
  File,
  UploadCloud,
  Plus,
  Info,
  RefreshCw,
  X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, addMonths, addYears, isAfter } from 'date-fns';
import { tr, de, enUS } from 'date-fns/locale';

function isValidPhoneNumber(value) {
  const v = String(value || '').trim();
  if (!v) return true;
  if (!/^\+\d/.test(v)) return false;
  if (!/^\+\d[\d\s().-]*$/.test(v)) return false;
  const digits = (v.match(/\d/g) || []).length;
  return digits >= 6;
}

const customerSchema = z
  .object({
    first_name: z.string().min(1, 'Vorname ist erforderlich'),
    last_name: z.string().min(1, 'Nachname ist erforderlich'),
    // These flags drive conditional required rules.
    no_email: z.boolean().default(false),
    no_phone: z.boolean().default(false),
    email: z.string().email('Bitte eine gültige E-Mail eingeben').optional().or(z.literal('')),
    phone: z
      .string()
      .optional()
      .refine((v) => isValidPhoneNumber(v), {
        message: 'Telefonnummer muss mit „+“ und Landesvorwahl beginnen (z.B. +49 …)',
      }),
    birth_date: z.string().min(1, 'Geburtsdatum ist erforderlich'),
    address: z.string().min(1, 'Adresse ist erforderlich'),
    city: z.string().optional(),
    zip_code: z
      .string()
      .min(1, 'Postleitzahl ist erforderlich')
      .refine((v) => /^\d+$/.test(String(v).trim()), {
        message: 'Postleitzahl darf nur Zahlen enthalten',
      }),
  })
  .superRefine((val, ctx) => {
    if (!val.no_email && !String(val.email || '').trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['email'],
        message: 'E-Mail ist erforderlich (oder „Keine E-Mail“ aktivieren)',
      });
    }
    if (!val.no_phone && !String(val.phone || '').trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['phone'],
        message: 'Telefon ist erforderlich (oder „Keine Telefonnummer“ aktivieren)',
      });
    }
  });

const contractSchema = z.object({
  customer_id: z.string().min(1, 'Required'),
  company_id: z.string().optional(),
  contract_type_id: z.string().optional(),
  // title: z.string().min(1, 'Required'), // Removed from UI as per image, will assume auto-generated or optional if backend allows. Making optional for now or hidden.
  start_date: z.string().min(1, 'Required'),
  end_date: z.string().min(1, 'Required'),
  order_number: z.string().optional(),
  contract_number: z.string().optional(),
  description: z.string().optional(),
  status: z.string().default('active'),
}).refine((data) => {
  if (data.start_date && data.end_date) {
    return isAfter(new Date(data.end_date), new Date(data.start_date));
  }
  return true;
}, {
  message: "End date must be after start date",
  path: ["end_date"],
});

export default function NewContract() {
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language === 'tr' ? tr : i18n.language === 'de' ? de : enUS;
  const navigate = useNavigate();
  const [customerResults, setCustomerResults] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [uniqueErrors, setUniqueErrors] = useState({ order: null, contract: null });
  const [isCustomerDetailOpen, setIsCustomerDetailOpen] = useState(false);
  const [detailCustomer, setDetailCustomer] = useState(null);
  const [detailCustomerContracts, setDetailCustomerContracts] = useState([]);
  const [step2Errors, setStep2Errors] = useState({ company: false, contractType: false, endDate: false });
  const [dragActive, setDragActive] = useState(false);

  const handleViewCustomerDetail = async (customer) => {
    setDetailCustomer(customer);
    try {
      const response = await customersAPI.getContracts(customer.id);
      setDetailCustomerContracts(response.data);
    } catch (error) {
      setDetailCustomerContracts([]);
    }
    setIsCustomerDetailOpen(true);
  };

  const formatDateDisplay = (value) => {
    const v = String(value || '').trim();
    if (!v) return '-';
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
    return v;
  };

  const checkUniqueness = async (field, value) => {
    if (!value || !value.trim()) return;
    try {
      const params = { [field === 'order_number' ? 'order_number' : 'contract_number']: value };
      const res = await contractsAPI.checkUniqueness(params);
      if (res.data.exists) {
        setUniqueErrors(prev => ({
          ...prev,
          [field === 'order_number' ? 'order' : 'contract']: field === 'order_number' ? 'contracts.orderNumberExists' : 'contracts.contractNumberExists'
        }));
      } else {
        setUniqueErrors(prev => ({ ...prev, [field === 'order_number' ? 'order' : 'contract']: null }));
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Generate unique number in format EPOS + 14 digits
  // Format: EPOS + YYMMDDHHMMSS + 2 random digits for uniqueness
  // Example: EPOS02001237598719
  const generateUniqueNumber = () => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2); // Last 2 digits of year (e.g., 24 for 2024)
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    // Add 2 random digits for additional uniqueness
    const random = String(Math.floor(Math.random() * 100)).padStart(2, '0');
    // Format: EPOS + YYMMDDHHMMSS + 2 random digits = 14 digits total
    const numberPart = `${year}${month}${day}${hours}${minutes}${seconds}${random}`;
    return `EPOS${numberPart}`;
  };

  const handleGenerateNewNumber = (field) => {
    const newNumber = generateUniqueNumber();
    contractForm.setValue(field, newNumber, { shouldDirty: true, shouldTouch: true });
    checkUniqueness(field, newNumber);
  };

  const contractForm = useForm({
    resolver: zodResolver(contractSchema),
    defaultValues: {
      status: 'active',
      start_date: format(new Date(), 'yyyy-MM-dd'),
    }
  });

  const customerForm = useForm({
    resolver: zodResolver(customerSchema),
  });

  const watchedBirthDate = customerForm.watch('birth_date');

  const watchStartDate = contractForm.watch('start_date');
  const watchEndDate = contractForm.watch('end_date');
  const selectedCustomerId = contractForm.watch('customer_id');
  const selectedCompanyId = contractForm.watch('company_id');
  const selectedContractTypeId = contractForm.watch('contract_type_id');

  useEffect(() => {
    fetchData();
    // Generate and set unique numbers when form is first opened
    const orderNumber = generateUniqueNumber();
    const contractNumber = generateUniqueNumber();
    contractForm.setValue('order_number', orderNumber);
    contractForm.setValue('contract_number', contractNumber);
  }, []);

  // Ensure numbers are set when step 2 is reached (in case they were cleared)
  useEffect(() => {
    if (step === 2) {
      const currentOrderNumber = contractForm.getValues('order_number');
      const currentContractNumber = contractForm.getValues('contract_number');

      if (!currentOrderNumber || currentOrderNumber.trim() === '') {
        const orderNumber = generateUniqueNumber();
        contractForm.setValue('order_number', orderNumber);
      }

      if (!currentContractNumber || currentContractNumber.trim() === '') {
        const contractNumber = generateUniqueNumber();
        contractForm.setValue('contract_number', contractNumber);
      }
    }
  }, [step]);

  const fetchData = async () => {
    try {
      const [companiesRes, typesRes] = await Promise.all([
        companiesAPI.getAll(),
        contractTypesAPI.getAll(),
      ]);
      setCompanies(companiesRes.data);
      setContractTypes(typesRes.data);
    } catch (error) {
      toast.error('Error loading data');
    }
  };

  const handleDurationSelect = (months) => {
    if (watchStartDate) {
      const startDate = new Date(watchStartDate);
      const endDate = months === 24 ? addYears(startDate, 2) : addMonths(startDate, months);
      contractForm.setValue('end_date', format(endDate, 'yyyy-MM-dd'));
    }
  };

  const [noEmail, setNoEmail] = useState(false);
  const [noPhone, setNoPhone] = useState(false);

  const handleAddCustomer = async (data) => {
    setAddingCustomer(true);
    try {
      const noEmailSelected = data?.no_email === true;
      const noPhoneSelected = data?.no_phone === true;
      const payload = {
        ...data,
        email: noEmailSelected ? null : (data.email || null),
        phone: noPhoneSelected ? null : (data.phone || null),
        birth_date: data.birth_date || null,
        address: data.address || null,
        zip_code: data.zip_code || null,
      };

      // Do not store flags in DB
      delete payload.no_email;
      delete payload.no_phone;

      const response = await customersAPI.create(payload);

      const newCustomer = response.data;
      setSelectedCustomer(newCustomer);
      setCustomerResults((prev) => [newCustomer, ...prev]);

      // Auto select and populate search
      contractForm.setValue('customer_id', newCustomer.id);
      setSearchQuery(`${newCustomer.first_name} ${newCustomer.last_name}`);

      toast.success(t('customer.added'));
      setIsAddCustomerOpen(false);
      customerForm.reset();
      setNoEmail(false);
      setNoPhone(false);
    } catch (error) {
      toast.error(t('customer.addError'));
    } finally {
      setAddingCustomer(false);
    }
  };

  const handleOpenAddCustomer = () => {
    setNoEmail(false);
    setNoPhone(false);
    customerForm.reset({
      first_name: '',
      last_name: '',
      no_email: false,
      no_phone: false,
      email: '',
      phone: '',
      birth_date: '',
      address: '',
      city: '',
      zip_code: '',
    });
    setIsAddCustomerOpen(true);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setUploadFiles((prev) => [...prev, ...files]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      setUploadFiles((prev) => [...prev, ...files]);
    }
  };

  const handleSubmit = async (data) => {
    // Prevent premature submission if not on final step
    if (step < 3) {
      setStep(step + 1);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...data,
        value: data.value ? parseFloat(data.value) : null,
        company_id: data.company_id || null,
        contract_type_id: data.contract_type_id || null,
      };

      const response = await contractsAPI.create(payload);

      // Upload files if any
      for (const file of uploadFiles) {
        await contractsAPI.uploadFile(response.data.id, file);
      }

      toast.success(t('contract.createSuccess'));
      navigate('/contracts');
    } catch (error) {
      console.error('Submission error:', error);
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        // Handle Pydantic validation errors array
        toast.error(detail.map(e => e.msg).join(', '));
      } else {
        toast.error(t('customer.addError') || 'Operation failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const normalizedSearch = useMemo(() => (searchQuery || '').trim(), [searchQuery]);

  // Server-side customer search (avoids the 1000-record limit of /customers)
  useEffect(() => {
    // If user already selected a customer and just sees the name, don't spam requests.
    if (!normalizedSearch) {
      setCustomerResults([]);
      setCustomerSearchLoading(false);
      return;
    }

    // Require at least 2 characters for search to keep it fast & relevant.
    if (normalizedSearch.length < 2) {
      setCustomerResults([]);
      setCustomerSearchLoading(false);
      return;
    }

    const tId = setTimeout(async () => {
      setCustomerSearchLoading(true);
      try {
        const res = await customersAPI.getPaged({ search: normalizedSearch, skip: 0, limit: 50 });
        setCustomerResults(res.data?.items || []);
      } catch (e) {
        // Don't toast on every keystroke; just fail silently.
        setCustomerResults([]);
      } finally {
        setCustomerSearchLoading(false);
      }
    }, 250);

    return () => clearTimeout(tId);
  }, [normalizedSearch]);

  const customerSuggestions = useMemo(() => {
    // Deduplicate by id (in case we prepended a newly created customer)
    const seen = new Set();
    const out = [];
    for (const c of customerResults || []) {
      if (!c?.id || seen.has(c.id)) continue;
      seen.add(c.id);
      out.push(c);
    }
    return out;
  }, [customerResults]);

  // Filter active companies (company select)
  const activeCompanies = companies.filter(c => c.status !== false);

  // Deduplicate companies by NAME in the dropdown.
  // In case multiple company records share the same name, selecting the name should
  // show contract types across all those company ids.
  const companyGroups = activeCompanies.reduce((acc, c) => {
    const key = (c?.name || '').trim();
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  const companyNameOptions = Object.keys(companyGroups).sort((a, b) => a.localeCompare(b));
  const selectedCompanyIdsForName = selectedCompanyName
    ? new Set((companyGroups[selectedCompanyName] || []).map((c) => c.id))
    : new Set();

  // Company must be selected first. Show ALL contract types belonging to that company NAME group.
  const selectableContractTypes = selectedCompanyName
    ? contractTypes
      .filter((t) => selectedCompanyIdsForName.has(t.company_id))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    : [];

  // Keep selectedCompanyName in sync when company_id changes (e.g., when a type auto-sets company_id)
  useEffect(() => {
    if (!selectedCompanyId) return;
    const c = companies.find((x) => x.id === selectedCompanyId);
    const name = (c?.name || '').trim();
    if (name && name !== selectedCompanyName) {
      setSelectedCompanyName(name);
    }
  }, [selectedCompanyId, companies, selectedCompanyName]);

  // If user changes company name selection and currently selected type doesn't belong to it, clear type
  useEffect(() => {
    if (!selectedCompanyName) return;
    if (!selectedContractTypeId) return;
    const type = contractTypes.find((t) => t.id === selectedContractTypeId);
    if (type && type.company_id && !selectedCompanyIdsForName.has(type.company_id)) {
      contractForm.setValue('contract_type_id', '');
    }
  }, [selectedCompanyName, selectedContractTypeId, contractTypes, contractForm, selectedCompanyIdsForName]);

  const canProceedStep1 = !!selectedCustomerId;
  const canProceedStep2 = contractForm.watch('title') && contractForm.watch('start_date') && contractForm.watch('end_date');

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="new-contract-page">
        {/* Header - Simple clean header or just stepper */}
        <div className="bg-white dark:bg-[#333333] p-6 rounded-lg shadow-sm border dark:border-gray-700">
          {/* Stepper */}
          <div className="flex items-center justify-between max-w-3xl mx-auto py-4">
            {[
              { num: 1, label: t('wizard.step1'), icon: UserPlus },
              { num: 2, label: t('wizard.step2'), icon: FileText },
              { num: 3, label: t('wizard.step3'), icon: File },
            ].map((s, index) => (
              <div key={s.num} className="flex flex-1 items-center last:flex-none">
                <div className="flex flex-col items-center relative z-10">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors border-2
                        ${step >= s.num
                        ? 'bg-zinc-800 text-white border-zinc-800 dark:bg-zinc-200 dark:text-[#333333] dark:border-zinc-200'
                        : 'bg-white text-gray-400 border-gray-200 dark:bg-[#444444] dark:text-gray-500 dark:border-gray-700'
                      }`}
                  >
                    <s.icon className="w-4 h-4" />
                  </div>
                  <span className={`text-xs mt-2 font-medium ${step >= s.num ? 'text-zinc-800 dark:text-gray-200' : 'text-gray-400'}`}>
                    {s.label}
                  </span>
                </div>
                {index < 2 && (
                  <div className={`flex-1 h-[1px] mx-4 -mt-6 ${step > s.num ? 'bg-zinc-800 dark:bg-zinc-200' : 'bg-gray-200 dark:bg-gray-700'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={contractForm.handleSubmit(handleSubmit)}>
          {/* Ensure Select-driven fields are registered so values are included in submit payload */}
          <input type="hidden" {...contractForm.register('company_id')} />
          <input type="hidden" {...contractForm.register('contract_type_id')} />

          {/* Step 1: Select Customer */}
          {step === 1 && (
            <Card className="min-h-[400px] dark:bg-[#333333] dark:border-none">
              <CardContent className="p-8 space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-700 dark:text-white mb-4">{t('wizard.step1')}</h2>
                  <div className="space-y-4">
                    <Input
                      placeholder={t('wizard.searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSearchQuery(v);
                        // If user starts typing after selection, clear selection.
                        if (selectedCustomerId && selectedCustomer) {
                          const selectedName = `${selectedCustomer.first_name} ${selectedCustomer.last_name}`.trim();
                          if (v.trim() !== selectedName) {
                            contractForm.setValue('customer_id', '');
                            setSelectedCustomer(null);
                          }
                        }
                      }}
                      className="h-12 dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                      data-testid="customer-search-input"
                    />

                    <div
                      className="flex items-center gap-2 text-zinc-800 dark:text-zinc-300 font-medium cursor-pointer hover:underline w-fit"
                      onClick={handleOpenAddCustomer}
                    >
                      <Plus className="w-5 h-5 bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 rounded-full p-1" />
                      {t('wizard.addNewCustomer')}
                    </div>
                  </div>
                </div>

                {customerSearchLoading && normalizedSearch.length >= 2 && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">{t('loading') || 'Loading...'}</div>
                )}

                {customerSuggestions.length > 0 && normalizedSearch && (
                  <div className="border rounded-lg max-h-[300px] overflow-y-auto mt-4 dark:border-gray-700">
                    {customerSuggestions.map((customer) => (
                      <div
                        key={customer.id}
                        className={`p-4 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#444444] flex justify-between items-center dark:border-gray-700
                                ${selectedCustomerId === customer.id ? 'bg-blue-50/50 dark:bg-[#4b5563]' : ''}`}
                        onClick={() => {
                          contractForm.setValue('customer_id', customer.id);
                          setSearchQuery(`${customer.first_name} ${customer.last_name}`);
                          setSelectedCustomer(customer);
                        }}
                      >
                        <span className="font-medium dark:text-white">{customer.first_name} {customer.last_name}</span>
                        <Info
                          className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewCustomerDetail(customer);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 2: Contract Details */}
          {step === 2 && (
            <Card className="dark:bg-[#333333] dark:border-none">
              <CardContent className="p-8 space-y-6">
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('contract.company')}</Label>
                    <Select
                      value={selectedCompanyName || ''}
                      onValueChange={(name) => {
                        setSelectedCompanyName(name);
                        const first = (companyGroups[name] || [])[0] || null;
                        contractForm.setValue('company_id', first?.id || '');
                        // Reset type when company changes to prevent mismatches
                        contractForm.setValue('contract_type_id', '');
                      }}
                    >
                      <SelectTrigger className={`h-11 bg-white dark:bg-[#444444] dark:border-gray-700 dark:text-white ${step2Errors.company ? 'border-red-500 ring-red-500' : ''}`}>
                        <SelectValue placeholder={t('contracts.selectCompanyLabel')} />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-[#444444] dark:border-gray-700">
                        {companyNameOptions.map((name) => (
                          <SelectItem key={name} value={name} className="dark:text-white dark:focus:bg-[#555555]">{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('contract.type')}</Label>
                    <Select
                      disabled={!selectedCompanyName}
                      value={selectedContractTypeId || ''}
                      onValueChange={(value) => {
                        contractForm.setValue('contract_type_id', value);
                        // Safety: ensure company matches type (should already be filtered)
                        const type = contractTypes.find((t) => t.id === value);
                        if (type?.company_id) {
                          contractForm.setValue('company_id', type.company_id);
                          const c = companies.find((x) => x.id === type.company_id);
                          const nm = (c?.name || '').trim();
                          if (nm) setSelectedCompanyName(nm);
                        }
                      }}
                    >
                      <SelectTrigger className={`h-11 bg-white dark:bg-[#444444] dark:border-gray-700 dark:text-white ${step2Errors.contractType ? 'border-red-500 ring-red-500' : ''}`}>
                        <SelectValue
                          placeholder={
                            selectedCompanyName ? t('contracts.selectContractType') : 'Select a company first'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-[#444444] dark:border-gray-700">
                        {selectableContractTypes.map((type) => {
                          const parentCompany = companies.find((c) => c.id === type.company_id);
                          const isDisabled = type.status === false || parentCompany?.status === false;
                          return (
                            <SelectItem key={type.id} value={type.id} disabled={isDisabled} className="dark:text-white dark:focus:bg-[#555555]">
                              {type.name}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('contract.startDate')}</Label>
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('contract.startDate')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full justify-start text-left font-normal h-11 dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]",
                            !watchStartDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {watchStartDate ? (
                            format(new Date(watchStartDate), "PPP", { locale: currentLocale })
                          ) : (
                            <span>{t('contract.startDate')}</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 dark:bg-[#333333] dark:border-gray-700" align="start">
                        <Calendar
                          mode="single"
                          selected={watchStartDate ? new Date(watchStartDate) : undefined}
                          onSelect={(date) => {
                            if (date) {
                              contractForm.setValue('start_date', format(date, 'yyyy-MM-dd'), { shouldValidate: true });
                            }
                          }}
                          initialFocus
                          captionLayout="dropdown-buttons"
                          fromYear={1900}
                          toYear={new Date().getFullYear() + 10}
                          locale={currentLocale}
                          className="dark:bg-[#333333] dark:text-white"
                          classNames={{
                            day_selected: "bg-zinc-900 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200",
                            day_today: "bg-accent text-accent-foreground dark:bg-white/10",
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                    <input type="hidden" {...contractForm.register('start_date')} />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('contract.duration')}</Label>
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-full px-6 border-zinc-400 text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-[#555555] dark:hover:text-white"
                        onClick={() => handleDurationSelect(6)}
                      >
                        {t('contracts.6months')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-full px-6 border-zinc-400 text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-[#555555] dark:hover:text-white"
                        onClick={() => handleDurationSelect(12)}
                      >
                        {t('contracts.1year')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-full px-6 border-zinc-400 text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-[#555555] dark:hover:text-white"
                        onClick={() => handleDurationSelect(24)}
                      >
                        {t('contracts.2years')}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('contract.endDate')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full justify-start text-left font-normal h-11 dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]",
                            !watchEndDate && "text-muted-foreground",
                            step2Errors.endDate && "border-red-500"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {watchEndDate ? (
                            format(new Date(watchEndDate), "PPP", { locale: currentLocale })
                          ) : (
                            <span>{t('contract.endDate')}</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 dark:bg-[#333333] dark:border-gray-700" align="start">
                        <Calendar
                          mode="single"
                          selected={watchEndDate ? new Date(watchEndDate) : undefined}
                          onSelect={(date) => {
                            if (date) {
                              contractForm.setValue('end_date', format(date, 'yyyy-MM-dd'), { shouldValidate: true });
                            }
                          }}
                          initialFocus
                          captionLayout="dropdown-buttons"
                          fromYear={1900}
                          toYear={new Date().getFullYear() + 20}
                          locale={currentLocale}
                          className="dark:bg-[#333333] dark:text-white"
                          classNames={{
                            day_selected: "bg-zinc-900 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200",
                            day_today: "bg-accent text-accent-foreground dark:bg-white/10",
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                    <input type="hidden" {...contractForm.register('end_date')} />
                    {contractForm.formState.errors.end_date && (
                      <p className="text-sm text-red-500">{t('contract.dateError')}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('contract.orderNo')}</Label>
                    <div className="flex gap-2">
                      <Input
                        {...contractForm.register('order_number')}
                        className={`h-11 dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500 ${uniqueErrors.order ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                        placeholder={t('contract.orderNo')}
                        onBlur={(e) => checkUniqueness('order_number', e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-11 w-11 shrink-0 dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]"
                        onClick={() => handleGenerateNewNumber('order_number')}
                        title={t('common.refresh', { defaultValue: 'Yeni numara oluştur' })}
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                    {uniqueErrors.order && (
                      <p className="text-sm text-red-500">{t(uniqueErrors.order)}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('contract.contractNo')}</Label>
                    <div className="flex gap-2">
                      <Input
                        {...contractForm.register('contract_number')}
                        className={`h-11 dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500 ${uniqueErrors.contract ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                        placeholder={t('contract.contractNo')}
                        onBlur={(e) => checkUniqueness('contract_number', e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-11 w-11 shrink-0 dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]"
                        onClick={() => handleGenerateNewNumber('contract_number')}
                        title={t('common.refresh', { defaultValue: 'Yeni numara oluştur' })}
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                    {uniqueErrors.contract && (
                      <p className="text-sm text-red-500">{t(uniqueErrors.contract)}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('contract.description')}</Label>
                    <Textarea
                      {...contractForm.register('description')}
                      className="min-h-[100px] dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                    />
                  </div>

                </div>

                {/* Inline validation error message */}
                {(step2Errors.company || step2Errors.contractType || step2Errors.endDate) && (
                  <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                    <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                      {t('wizard.fillRequiredFields')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card >
          )
          }

          {/* Step 3: File Upload */}
          {
            step === 3 && (
              <Card className="dark:bg-[#333333] dark:border-none">
                <CardContent className="p-8 py-12">
                  <div
                    className={cn(
                      "relative border-2 border-dashed rounded-lg p-12 text-center flex flex-col items-center justify-center gap-4 cursor-pointer transition-all",
                      dragActive
                        ? "border-blue-500 bg-blue-50/30 dark:border-blue-400 dark:bg-blue-400/10 scale-[1.01]"
                        : "border-green-700/50 bg-green-50/20 dark:bg-white/5 dark:border-gray-700"
                    )}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <UploadCloud className={cn("w-12 h-12 transition-colors", dragActive ? "text-blue-500 dark:text-blue-400" : "text-green-700 dark:text-gray-400")} />
                    <div>
                      <p className="font-semibold text-zinc-800 dark:text-white text-lg">{t('contract.dragDrop')}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mt-1">{t('contract.supportedFormats')}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{t('contract.maxSize')}</p>
                    </div>
                    <Input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                      id="file-upload-wizard"
                    />
                    <Label htmlFor="file-upload-wizard" className="absolute inset-0 cursor-pointer"></Label>
                  </div>

                  {uploadFiles.length > 0 && (
                    <div className="space-y-2 mt-6">
                      {uploadFiles.map((file, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-white/5 rounded border dark:border-gray-700 group">
                          <FileText className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium dark:text-white truncate block">{file.name}</span>
                            <span className="text-xs text-gray-400">
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => setUploadFiles(prev => prev.filter((_, i) => i !== index))}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          }

          {/* Navigation Buttons */}
          <div className="flex justify-end gap-3 pt-6 pb-12">
            <Button
              type="button"
              className="bg-zinc-600 hover:bg-zinc-700 dark:bg-gray-700 dark:hover:bg-gray-600 text-white min-w-[120px]"
              onClick={() => step > 1 ? setStep(step - 1) : navigate('/contracts')}
            >
              {step > 1 ? t('wizard.back') : t('wizard.cancel')}
            </Button>

            {step < 3 ? (
              <Button
                key="next-btn"
                type="button"
                className="bg-zinc-800 hover:bg-zinc-900 dark:bg-zinc-700 dark:hover:bg-zinc-600 text-white min-w-[120px]"
                onClick={(e) => {
                  e.preventDefault();
                  if (step === 2) {
                    // Check if unique errors exist preventing progress
                    if (uniqueErrors.order || uniqueErrors.contract) {
                      toast.error(t('contracts.fixErrors'));
                      return;
                    }
                    // Validate required fields for step 2
                    const companyId = contractForm.getValues('company_id');
                    const contractTypeId = contractForm.getValues('contract_type_id');
                    const endDate = contractForm.getValues('end_date');

                    const errors = {
                      company: !companyId || !companyId.trim(),
                      contractType: !contractTypeId || !contractTypeId.trim(),
                      endDate: !endDate || !endDate.trim(),
                    };

                    setStep2Errors(errors);

                    if (errors.company || errors.contractType || errors.endDate) {
                      return;
                    }

                    // Check if end date is after start date
                    const startDate = contractForm.getValues('start_date');
                    if (startDate && endDate) {
                      if (!isAfter(new Date(endDate), new Date(startDate))) {
                        return;
                      }
                    }
                  } else {
                    // Clear errors when leaving step 2
                    setStep2Errors({ company: false, contractType: false, endDate: false });
                  }
                  setStep(step + 1);
                }}
                disabled={step === 1 && !selectedCustomerId}
              >
                {t('wizard.next')}
              </Button>
            ) : (
              <Button
                key="send-btn"
                type="submit"
                disabled={saving}
                className="bg-zinc-800 hover:bg-zinc-900 dark:bg-zinc-700 dark:hover:bg-zinc-600 text-white min-w-[120px]"
              >
                {saving ? <Loader2 className="animate-spin" /> : t('wizard.send')}
              </Button>
            )}
          </div>
        </form >

        {/* Add Customer Modal */}
        <Dialog open={isAddCustomerOpen} onOpenChange={setIsAddCustomerOpen}>
          {/* Outer container clips rounded corners; scroll happens inside */}
          <DialogContent className="sm:max-w-lg p-0 max-h-[98vh] overflow-hidden dark:bg-[#333333] dark:border-none dark:text-white" data-testid="customer-modal">
            <div className="p-4 max-h-[98vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="dark:text-white">
                  {t('customers.addNew')}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={customerForm.handleSubmit(handleAddCustomer)} className="space-y-2">
                {/* Hidden fields so zod can validate conditional requirements */}
                <input type="hidden" {...customerForm.register('no_email')} />
                <input type="hidden" {...customerForm.register('no_phone')} />
                <div className="space-y-1">
                  <Label htmlFor="first_name" className="dark:text-white">{t('customers.firstName')}</Label>
                  <Input
                    id="first_name"
                    {...customerForm.register('first_name')}
                    data-testid="customer-firstname-input"
                    className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                  />
                  {customerForm.formState.errors.first_name && (
                    <p className="text-sm text-destructive">{customerForm.formState.errors.first_name.message}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="last_name" className="dark:text-white">{t('customers.lastName')}</Label>
                  <Input
                    id="last_name"
                    {...customerForm.register('last_name')}
                    data-testid="customer-lastname-input"
                    className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                  />
                  {customerForm.formState.errors.last_name && (
                    <p className="text-sm text-destructive">{customerForm.formState.errors.last_name.message}</p>
                  )}
                </div>

                {/* Group email + phone to reduce gap between them */}
                <div className="space-y-0">
                  <div className="space-y-1">
                    <Label htmlFor="email" className="dark:text-white">{t('customers.email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      {...customerForm.register('email')}
                      data-testid="customer-email-input"
                      disabled={noEmail}
                      className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                    />
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-xs text-muted-foreground dark:text-gray-400">
                        {t('customers.noEmail', { defaultValue: 'Keine E-Mail' })}
                      </span>
                      <Switch
                        checked={noEmail}
                        onCheckedChange={(checked) => {
                          setNoEmail(checked);
                          customerForm.setValue('no_email', checked, { shouldValidate: true });
                          if (checked) customerForm.setValue('email', '');
                        }}
                        className="dark:data-[state=unchecked]:bg-[#444444] dark:data-[state=checked]:bg-zinc-200"
                      />
                    </div>
                    {customerForm.formState.errors.email && (
                      <p className="text-sm text-destructive">{customerForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-1 -mt-2">
                    <Label htmlFor="phone" className="dark:text-white">{t('customers.phone')}</Label>
                    <Input
                      id="phone"
                      {...customerForm.register('phone')}
                      data-testid="customer-phone-input"
                      inputMode="tel"
                      placeholder="+49 123 456789"
                      disabled={noPhone}
                      className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                    />
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-xs text-muted-foreground dark:text-gray-400">
                        {t('customers.noPhone', { defaultValue: 'Keine Telefonnummer' })}
                      </span>
                      <Switch
                        checked={noPhone}
                        onCheckedChange={(checked) => {
                          setNoPhone(checked);
                          customerForm.setValue('no_phone', checked, { shouldValidate: true });
                          if (checked) customerForm.setValue('phone', '');
                        }}
                        className="dark:data-[state=unchecked]:bg-[#444444] dark:data-[state=checked]:bg-zinc-200"
                      />
                    </div>
                    {customerForm.formState.errors.phone && (
                      <p className="text-sm text-destructive">{customerForm.formState.errors.phone.message}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="birth_date" className="dark:text-white">{t('customer.birthDate', { defaultValue: 'Doğum Tarihi' })}</Label>
                  <Label htmlFor="birth_date" className="dark:text-white">{t('customer.birthDate', { defaultValue: 'Doğum Tarihi' })}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full justify-start text-left font-normal dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]",
                          !watchedBirthDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {watchedBirthDate ? (
                          format(new Date(watchedBirthDate), "PPP", { locale: currentLocale })
                        ) : (
                          <span>{t('customer.birthDate', { defaultValue: 'Doğum Tarihi' })}</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 dark:bg-[#333333] dark:border-gray-700" align="start">
                      <Calendar
                        mode="single"
                        selected={watchedBirthDate ? new Date(watchedBirthDate) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            // Format as YYYY-MM-DD for consistency with backend/schema
                            customerForm.setValue('birth_date', format(date, 'yyyy-MM-dd'), { shouldValidate: true });
                          }
                        }}
                        initialFocus
                        captionLayout="dropdown-buttons"
                        fromYear={1900}
                        toYear={new Date().getFullYear()}
                        locale={currentLocale}
                        className="dark:bg-[#333333] dark:text-white"
                        classNames={{
                          day_selected: "bg-zinc-900 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200",
                          day_today: "bg-accent text-accent-foreground dark:bg-white/10",
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                  <input type="hidden" {...customerForm.register('birth_date')} />
                  {customerForm.formState.errors.birth_date && (
                    <p className="text-sm text-destructive">{customerForm.formState.errors.birth_date.message}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="address" className="dark:text-white">{t('customers.address')}</Label>
                  <Textarea
                    id="address"
                    rows={2}
                    {...customerForm.register('address')}
                    data-testid="customer-address-input"
                    className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                  />
                  {customerForm.formState.errors.address && (
                    <p className="text-sm text-destructive">{customerForm.formState.errors.address.message}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="zip_code" className="dark:text-white">{t('customer.postalCode', { defaultValue: 'Posta Kodu' })}</Label>
                  <Input
                    id="zip_code"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="12345"
                    {...customerForm.register('zip_code')}
                    className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                  />
                  {customerForm.formState.errors.zip_code && (
                    <p className="text-sm text-destructive">{customerForm.formState.errors.zip_code.message}</p>
                  )}
                </div>

                <DialogFooter className="dark:border-t dark:border-gray-700 pt-4 mt-4">
                  <Button type="button" variant="outline" onClick={() => setIsAddCustomerOpen(false)} className="dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-700 dark:text-white">
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={addingCustomer} data-testid="customer-save-btn" className="min-w-[100px] dark:bg-zinc-700 dark:hover:bg-zinc-600">
                    {addingCustomer ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('common.loading')}
                      </>
                    ) : (
                      t('common.save')
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </div>
          </DialogContent>
        </Dialog>

        {/* Customer Detail Modal */}
        <Dialog open={isCustomerDetailOpen} onOpenChange={setIsCustomerDetailOpen}>
          <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto dark:bg-[#333333] dark:border-none dark:text-white" data-testid="customer-detail-modal">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                {detailCustomer?.first_name} {detailCustomer?.last_name}
              </DialogTitle>
            </DialogHeader>

            {detailCustomer && (
              <div className="space-y-6">
                {/* Customer Information */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground dark:text-gray-400 mb-2">
                    {t('customers.detailCustomerInfoTitle', { defaultValue: 'Müşteri Bilgileri' })}
                  </h3>
                  <div className="rounded-md border dark:border-gray-700 overflow-hidden">
                    <div className="grid grid-cols-3 text-sm">
                      <div className="col-span-1 px-4 py-3 border-b dark:border-gray-700 font-medium dark:bg-white/5">
                        {t('customers.detailCustomerLabel', { defaultValue: 'Müşteri:' })}
                      </div>
                      <div className="col-span-2 px-4 py-3 border-b dark:border-gray-700">
                        {detailCustomer.first_name} {detailCustomer.last_name}
                      </div>

                      <div className="col-span-1 px-4 py-3 border-b dark:border-gray-700 font-medium dark:bg-white/5">E-mail:</div>
                      <div className="col-span-2 px-4 py-3 border-b dark:border-gray-700">{detailCustomer.email || '-'}</div>

                      <div className="col-span-1 px-4 py-3 border-b dark:border-gray-700 font-medium dark:bg-white/5">
                        {t('customers.phone', { defaultValue: 'Telefon' })}:
                      </div>
                      <div className="col-span-2 px-4 py-3 border-b dark:border-gray-700 font-mono">{detailCustomer.phone || '-'}</div>

                      <div className="col-span-1 px-4 py-3 border-b dark:border-gray-700 font-medium dark:bg-white/5">
                        {t('customer.birthDate', { defaultValue: 'Doğum Tarihi' })}:
                      </div>
                      <div className="col-span-2 px-4 py-3 border-b dark:border-gray-700">
                        {formatDateDisplay(detailCustomer.birth_date)}
                      </div>

                      <div className="col-span-1 px-4 py-3 border-b dark:border-gray-700 font-medium dark:bg-white/5">
                        {t('customer.postalCode', { defaultValue: 'Posta Kodu' })}:
                      </div>
                      <div className="col-span-2 px-4 py-3 border-b dark:border-gray-700">
                        {(detailCustomer.zip_code || '-')}{detailCustomer.city ? ` ${detailCustomer.city}` : ''}
                      </div>

                      <div className="col-span-1 px-4 py-3 font-medium dark:bg-white/5">
                        {t('customers.address', { defaultValue: 'Adres' })}:
                      </div>
                      <div className="col-span-2 px-4 py-3">
                        {detailCustomer.address || '-'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contracts */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                    {t('customers.detailContractsTitle', { defaultValue: 'Sözleşmeler' })}
                  </h3>
                  <div className="rounded-md border overflow-hidden dark:border-gray-700">
                    <Table>
                      <TableHeader className="bg-muted/50 dark:bg-[#444444] dark:border-gray-700">
                        <TableRow>
                          <TableHead className="w-[140px] dark:text-white">
                            {t('contracts.contractNo', { defaultValue: 'Sözleşme No' })}
                          </TableHead>
                          <TableHead className="dark:text-white">{t('contracts.company', { defaultValue: 'Şirket' })}</TableHead>
                          <TableHead className="dark:text-white">{t('contracts.type', { defaultValue: 'Sözleşme Türü' })}</TableHead>
                          <TableHead className="w-[160px] dark:text-white">
                            {t('customers.detailDaysLeft', { defaultValue: 'Bitime Kalan Gün' })}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detailCustomerContracts.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                              {t('customers.detailNoContracts', { defaultValue: 'Sözleşme bulunamadı' })}
                            </TableCell>
                          </TableRow>
                        ) : (
                          detailCustomerContracts.map((contract) => (
                            <TableRow key={contract.id} className="table-row dark:border-gray-700/50 dark:hover:bg-[#444444]">
                              <TableCell className="font-medium">
                                {contract.contract_number || '-'}
                              </TableCell>
                              <TableCell className="truncate max-w-[220px]">
                                {contract.company_name || '-'}
                              </TableCell>
                              <TableCell className="truncate max-w-[220px]">
                                {contract.contract_type_name || '-'}
                              </TableCell>
                              <TableCell>
                                {contract.days_remaining !== null && contract.days_remaining !== undefined ? (
                                  <span className={contract.days_remaining < 30 ? "text-red-500 font-semibold" : ""}>
                                    {contract.days_remaining} {t('contracts.days', { defaultValue: 'Gün' })}
                                  </span>
                                ) : '-'}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div >
    </Layout >
  );
}
